import {
  buildPath,
  buildTemplatePath,
  CloseButton,
  formatDate,
  ListContext,
  ListContextValue,
  newToast,
  Page,
  PageRoute,
  pn,
  Stack,
  Typography,
  useUnsavedChanges,
  useValidationErrors,
} from "@compass/core";
import { BulkSelected, SubmitButton } from "components";
import {
  BulkSecondLevelValidation,
  BulkSelectedItemErrors,
  StaffService,
  StaffServiceBulkUpdate,
  StaffServiceBulkUpdateSchema,
  StaffServiceEditResource,
} from "generated/openApi";
import { useContext, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { setAppToast } from "redux/reducers/appSlice";
import ApiService from "services/apiService";
import { ServiceUpdateSchema } from "./StaffUpdateSchema/StaffUpdateSchema";

const routeName = "staffServiceBulkUpdate";

export const staffServiceBulkUpdatePageRoute: PageRoute = {
  handle: "Batch Update Services",
  templatePath: buildTemplatePath(routeName),
  getPath: () => buildPath(routeName),
};

export type StaffServiceBulkUpdatePageState = {
  selectedIds: number[];
};

export const StaffServiceBulkUpdatePage = () => {
  const dispatch = useDispatch();
  const listContext = useContext<ListContextValue>(ListContext);
  const { state } = useLocation();
  const [stateParam] = useState<StaffServiceBulkUpdatePageState>(state);
  const [bulkUpdate, setBulkUpdate] = useState<StaffServiceBulkUpdate>({} as StaffServiceBulkUpdate);
  const [services, setServices] = useState<Map<number, StaffService>>(new Map());
  const [resource, setResource] = useState<StaffServiceEditResource>({});
  const { unsavedChanges, resetUnsavedChanges } = useUnsavedChanges({ trackedItems: [bulkUpdate] });
  const { validationErrors, setValidationErrors } = useValidationErrors();
  const [selectedItemsErrors, setSelectedItemsErrors] = useState<BulkSelectedItemErrors[]>([]);

  useEffect(() => {
    getBulkUpdate(stateParam.selectedIds);
    fetchResource();
  }, []);

  const getBulkUpdate = (ids: number[]) => {
    ApiService.staffServices.getBulkUpdate(ids).then((response) => {
      setBulkUpdate(response);
      setServices(new Map(response.services?.map((x) => [x.id, x])));
      resetUnsavedChanges([response]);
    });
  };

  const fetchResource = () => {
    ApiService.staffServices.getBulkUpdateResource().then((response) => {
      setResource(response);
    });
  };

  const save = (bulkUpdate: StaffServiceBulkUpdate) => {
    setValidationErrors({});
    setSelectedItemsErrors([]);

    ApiService.staffServices
      .saveBulkUpdate({ ...bulkUpdate, services: Array.from(services.values()) })
      .then((response) => {
        const bulkSecondLevelValidation: BulkSecondLevelValidation | undefined =
          response as unknown as BulkSecondLevelValidation;

        if (bulkSecondLevelValidation && bulkSecondLevelValidation.badRequest) {
          dispatch(setAppToast(newToast("error")));
          setSelectedItemsErrors(bulkSecondLevelValidation.selectedItemsErrors || []);
        } else {
          dispatch(setAppToast(newToast("success")));
          resetUnsavedChanges([bulkUpdate]);
          listContext.loadList?.();
          listContext.clearSelectedIds?.();
        }
      })
      .catch((ex) => {
        dispatch(setAppToast(newToast("error")));
        setValidationErrors(ex.response);
      });
  };

  return (
    <Page
      isForm
      header={
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="inherit">{staffServiceBulkUpdatePageRoute.handle}</Typography>

          <Stack direction="row" alignItems="center" spacing="sm">
            <CloseButton confirm={unsavedChanges} />

            <SubmitButton
              onClick={() => save(bulkUpdate)}
              disabled={!(bulkUpdate?.services && bulkUpdate?.services?.length > 0 && unsavedChanges)}
            />
          </Stack>
        </Stack>
      }
    >
      <Stack spacing="md">
        <ServiceUpdateSchema
          updateSchema={bulkUpdate.updateSchema || ({} as StaffServiceBulkUpdateSchema)}
          setUpdateSchema={(value) => setBulkUpdate({ ...bulkUpdate, updateSchema: value })}
          resource={resource}
          validationErrors={validationErrors?.[pn<StaffServiceBulkUpdate>("updateSchema")] || []}
        />

        <BulkSelected
          label="Service"
          items={services || []}
          setItems={(value) => setServices(value)}
          getItemLabel={(service) => (
            <Stack>
              <Typography>
                {service.staff?.lastName}, {service.staff?.firstName}
              </Typography>
              <Typography variant="caption" lineHeight="100%">
                {formatDate(service.date)} {service.name ? ` - ${service.name}` : ""}
              </Typography>
            </Stack>
          )}
          errors={selectedItemsErrors}
          setErrors={setSelectedItemsErrors}
        />
      </Stack>
    </Page>
  );
};
